import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Stack,
    IconButton,
    Input,
    CircularProgress,
    FormControlLabel,
    Switch,
    Card
} from "@mui/material";
import { ArrowLeft, Upload, X, Plus, Trash2, CheckCircle2, HelpCircle } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { postCategory, updateCategory, getCategoryById } from "../../../Services/CategoryManagement/CategoryManagementApi";
import { useGlobalSnackbar } from "../../../Components/Common/GlobalSanckbar";

import { generateSlug, normalizeSlugInput, cleanSlugFinal } from "../../../utils.js/slugUtils";

const safeParseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
        return JSON.parse(val);
    } catch {
        return [];
    }
};

// Validation function to allow only letters, numbers, and spaces (for general fields)
const validateTextField = (value) => {
    // Allow only letters, numbers, and spaces - remove special characters
    const cleaned = value.replace(/[^a-zA-Z0-9\s]/g, '');
    // Remove multiple spaces between words, but keep leading/trailing spaces during typing
    const singleSpaced = cleaned.replace(/\s+/g, ' ');
    return singleSpaced;
};

// Validation function for slug (allows letters, numbers, hyphens, spaces, and special characters)
const validateSlugField = (value) => {
    // Allow all characters (letters, numbers, hyphens, spaces, and special characters)
    // Only normalize multiple spaces
    const singleSpaced = value.replace(/\s+/g, ' ');
    return singleSpaced;
};

const AddEditCategories = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const location = useLocation();
    const { id: paramId } = useParams();
    const categoryId = paramId || location.state?.category?.id || location.state?.id;
    const isEdit = Boolean(categoryId);

    const [pageLoading, setPageLoading] = useState(isEdit);

    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
    const [heading, setHeading] = useState("");
    const [description, setDescription] = useState("");
    const [displayOrder, setDisplayOrder] = useState(isEdit ? "" : "1");
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [icon, setIcon] = useState(null);
    const [iconPreview, setIconPreview] = useState(null);
    const [ogImage, setOgImage] = useState(null);
    const [ogImagePreview, setOgImagePreview] = useState(null);
    const [status, setStatus] = useState(true);

    // Suitability Items & FAQs state
    const [suitabilityItems, setSuitabilityItems] = useState([]);
    const [newSuitabilityInput, setNewSuitabilityInput] = useState('');

    const [faqs, setFaqs] = useState([]);
    const [newFaqQuestion, setNewFaqQuestion] = useState('');
    const [newFaqAnswer, setNewFaqAnswer] = useState('');

    // SEO fields
    const [metaTitle, setMetaTitle] = useState("");
    const [metaDescription, setMetaDescription] = useState("");
    const [metaOutlet, setMetaOutlet] = useState([]);
    const [newMetaOutletInput, setNewMetaOutletInput] = useState('');
    const [metaKeywords, setMetaKeywords] = useState("");
    const [imageAltText, setImageAltText] = useState("");
    const [canonicalUrl, setCanonicalUrl] = useState("");
    const [isIndex, setIsIndex] = useState(true);

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const nameFieldRef = useRef(null);
    const slugFieldRef = useRef(null);
    const headingFieldRef = useRef(null);
    const descriptionFieldRef = useRef(null);
    const displayOrderFieldRef = useRef(null);
    const imageFieldRef = useRef(null);
    const iconFieldRef = useRef(null);
    const ogImageFieldRef = useRef(null);
    const metaTitleFieldRef = useRef(null);
    const metaDescriptionFieldRef = useRef(null);
    const metaOutletFieldRef = useRef(null);
    const imageAltTextFieldRef = useRef(null);
    const canonicalUrlFieldRef = useRef(null);
    const isIndexFieldRef = useRef(null);

    useEffect(() => {
        if (categoryId) {
            const fetchCategory = async () => {
                setPageLoading(true);
                try {
                    const res = await getCategoryById(categoryId);
                    const data = res?.data || res?.category || res;
                    if (data) {
                        setName(data.name || "");
                        setSlug(data.slug ? cleanSlugFinal(data.slug) : (data.name ? generateSlug(data.name) : ""));
                        setIsSlugManuallyEdited(false);
                        setHeading(data.heading || "");
                        setDescription(data.description || "");
                        setDisplayOrder(data.displayOrder !== undefined && data.displayOrder !== null ? String(data.displayOrder) : "1");
                        setImagePreview(data.image || null);
                        setIconPreview(data.icon || null);
                        setOgImagePreview(data.ogImage || null);
                        setOgImage(null);
                        setStatus(data.status !== undefined ? data.status : true);
                        setSuitabilityItems(
                            safeParseArray(data.suitabilityItems)
                                .map(item => (typeof item === 'object' && item !== null ? item.name || item.title || item.label || '' : String(item || '')))
                                .filter(Boolean)
                        );
                        setFaqs(
                            safeParseArray(data.faqs).map(faq => ({
                                question: typeof faq?.question === 'object' && faq.question !== null ? faq.question.name || '' : (faq?.question || faq?.[0] || ''),
                                answer: typeof faq?.answer === 'object' && faq.answer !== null ? faq.answer.name || '' : (faq?.answer || faq?.[1] || '')
                            }))
                        );
                        setMetaTitle(data.metaTitle || "");
                        setMetaDescription(data.metaDescription || "");
                        setMetaOutlet(safeParseArray(data.metaOutlet) || []);
                        setMetaKeywords(data.metaKeywords || "");
                        setImageAltText(data.imageAltText || "");
                        setCanonicalUrl(data.canonicalUrl || "");
                        setIsIndex(data.isIndex !== undefined ? (data.isIndex === "true" || data.isIndex === true) : true);
                    }
                } catch (err) {
                    console.error("Error fetching category for edit:", err);
                    showSnackbar("Failed to fetch category details from API", "error");
                } finally {
                    setPageLoading(false);
                }
            };
            fetchCategory();
        }
    }, [categoryId, showSnackbar]);

    const handleNameChange = (e) => {
        const newName = validateTextField(e.target.value);
        setName(newName);
        if (fieldErrors.name) {
            setFieldErrors(prev => ({ ...prev, name: null }));
        }
        if (!isSlugManuallyEdited) {
            const autoSlug = generateSlug(newName);
            setSlug(autoSlug);
            if (fieldErrors.slug) {
                setFieldErrors(prev => ({ ...prev, slug: null }));
            }
        }
    };

    const handleSlugChange = (e) => {
        const rawVal = e.target.value;
        const normalizedVal = validateSlugField(rawVal);
        setSlug(normalizedVal);
        setIsSlugManuallyEdited(true);
        if (fieldErrors.slug) {
            setFieldErrors(prev => ({ ...prev, slug: null }));
        }
    };

    const handleSlugBlur = () => {
        if (!slug) {
            setSlug("");
            return;
        }
        const cleaned = validateSlugField(slug);
        if (cleaned !== slug) {
            setSlug(cleaned);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        const inputElement = e.target;
        if (file) {
            if (!file.type.startsWith('image/')) {
                setFieldErrors(prev => ({
                    ...prev,
                    categoryImage: 'Only image files are allowed.'
                }));
                setImage(null);
                setImagePreview(null);
                inputElement.value = "";
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.onload = () => {
                    if (img.width < 400 && img.height < 400) {
                        setFieldErrors(prev => ({
                            ...prev,
                            categoryImage: `Image "${file.name}" is too small. At least one dimension must be ≥ 400px.`
                        }));
                        setImage(null);
                        setImagePreview(null);
                        inputElement.value = "";
                    } else {
                        setImage(file);
                        setImagePreview(reader.result);
                        setFieldErrors(prev => ({ ...prev, categoryImage: null }));
                    }
                };
                img.src = reader.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setImagePreview(null);
        if (fieldErrors.categoryImage) {
            setFieldErrors(prev => ({ ...prev, categoryImage: null }));
        }
    };

    const handleIconChange = (e) => {
        const file = e.target.files[0];
        const inputElement = e.target;
        if (file) {
            if (!file.type.startsWith('image/')) {
                setFieldErrors(prev => ({
                    ...prev,
                    categoryIcon: 'Only image files are allowed.'
                }));
                setIcon(null);
                setIconPreview(null);
                inputElement.value = "";
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.onload = () => {
                    if (img.width < 400 && img.height < 400) {
                        setFieldErrors(prev => ({
                            ...prev,
                            categoryIcon: `Icon "${file.name}" is too small. At least one dimension must be ≥ 400px.`
                        }));
                        setIcon(null);
                        setIconPreview(null);
                        inputElement.value = "";
                    } else {
                        setIcon(file);
                        setIconPreview(reader.result);
                        setFieldErrors(prev => ({ ...prev, categoryIcon: null }));
                    }
                };
                img.src = reader.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveIcon = () => {
        setIcon(null);
        setIconPreview(null);
        if (fieldErrors.categoryIcon) {
            setFieldErrors(prev => ({ ...prev, categoryIcon: null }));
        }
    };

    const handleOgImageChange = (e) => {
        const file = e.target.files[0];
        const inputElement = e.target;
        if (file) {
            if (!file.type.startsWith('image/')) {
                setFieldErrors(prev => ({
                    ...prev,
                    ogImage: 'Only image files are allowed.'
                }));
                setOgImage(null);
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                setFieldErrors(prev => ({
                    ...prev,
                    ogImage: 'Image size must be less than 2MB.'
                }));
                setOgImage(null);
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setOgImage(file);
                setOgImagePreview(reader.result);
                setFieldErrors(prev => ({ ...prev, ogImage: null }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveOgImage = () => {
        setOgImage(null);
        setOgImagePreview(null);
        if (fieldErrors.ogImage) {
            setFieldErrors(prev => ({ ...prev, ogImage: null }));
        }
    };

    // Suitability Item Handlers
    const handleAddSuitabilityItem = () => {
        const trimmed = newSuitabilityInput.trim();
        if (!trimmed) return;
        setSuitabilityItems(prev => [...prev, trimmed]);
        setNewSuitabilityInput('');
    };

    const handleRemoveSuitabilityItem = (index) => {
        setSuitabilityItems(prev => prev.filter((_, idx) => idx !== index));
    };

    // FAQ Handlers
    const handleAddFaq = () => {
        const qTrimmed = newFaqQuestion.trim();
        const aTrimmed = newFaqAnswer.trim();
        if (!qTrimmed || !aTrimmed) {
            showSnackbar("Please fill both Question and Answer for the FAQ", "warning");
            return;
        }
        setFaqs(prev => [...prev, { question: qTrimmed, answer: aTrimmed }]);
        setNewFaqQuestion('');
        setNewFaqAnswer('');
    };

    const handleRemoveFaq = (index) => {
        setFaqs(prev => prev.filter((_, idx) => idx !== index));
    };

    // Meta Outlet Handlers
    const handleAddMetaOutletItem = () => {
        const trimmed = newMetaOutletInput.trim();
        if (!trimmed) return;
        setMetaOutlet(prev => [...prev, trimmed]);
        setNewMetaOutletInput('');
    };

    const handleRemoveMetaOutletItem = (index) => {
        setMetaOutlet(prev => prev.filter((_, idx) => idx !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFieldErrors({});

        const errors = {};
        if (!name || !name.trim()) {
            errors.name = "Please provide a category name.";
        }
        const finalSlug = cleanSlugFinal(slug);
        if (!finalSlug) {
            errors.slug = "Please provide a category slug.";
        }
        if (description && description.length > 100) {
            errors.description = "Description cannot exceed 100 characters.";
        }
        if (displayOrder !== "" && displayOrder !== null && displayOrder !== undefined && (Number(displayOrder) > 999 || Number(displayOrder) < 1)) {
            errors.displayOrder = "Display order must be between 1 and 999.";
        }
        if (!categoryId && !image) {
            errors.categoryImage = "Please provide an image.";
        }
        if (!categoryId && !icon) {
            errors.categoryIcon = "Please provide an icon.";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setTimeout(() => {
                if (errors.name) {
                    nameFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (errors.slug) {
                    slugFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (errors.heading) {
                    headingFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (errors.description) {
                    descriptionFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (errors.displayOrder) {
                    displayOrderFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (errors.categoryImage) {
                    imageFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (errors.categoryIcon) {
                    iconFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (errors.imageAltText) {
                    imageAltTextFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (errors.canonicalUrl) {
                    canonicalUrlFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (errors.isIndex) {
                    isIndexFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 50);
            return;
        }

        setLoading(true);

        const formData = new FormData();
        formData.append("name", name.trim());
        formData.append("slug", finalSlug);
        formData.append("heading", heading);
        formData.append("description", description);
        if (displayOrder !== "" && displayOrder !== null && displayOrder !== undefined) {
            formData.append("displayOrder", displayOrder);
        }
        if (image) {
            formData.append("categoryImage", image);
        }
        if (icon) {
            formData.append("categoryIcon", icon);
        }
        if (ogImage) {
            formData.append("ogImage", ogImage);
        } else if (categoryId && !ogImagePreview) {
            formData.append("ogImage", "");
        }

        formData.append("status", status);
        formData.append("suitabilityItems", JSON.stringify(suitabilityItems));
        formData.append("faqs", JSON.stringify(faqs));
        formData.append("metaTitle", metaTitle);
        formData.append("metaDescription", metaDescription);
        formData.append("metaOutlet", JSON.stringify(metaOutlet));
        formData.append("metaKeywords", metaKeywords);
        formData.append("imageAltText", imageAltText);
        formData.append("canonicalUrl", canonicalUrl);
        formData.append("isIndex", isIndex ? "true" : "false");

        try {
            if (categoryId) {
                await updateCategory(categoryId, formData);
                showSnackbar("Category updated successfully!", "success");
                setSuccess(true);
            } else {
                await postCategory(formData);
                showSnackbar("Category created successfully!", "success");
                setSuccess(true);
            }
            setTimeout(() => {
                navigate("/category-management");
            }, 1500);
        } catch (err) {
            const errorData = err.response?.data;
            if (errorData && errorData.details && Array.isArray(errorData.details)) {
                const errorsObj = {};
                errorData.details.forEach(detail => {
                    const fieldName = (detail.field === "image" || detail.field === "file") ? "categoryImage" :
                        (detail.field === "icon") ? "categoryIcon" :
                            (detail.field === "ogImage" || detail.field === "og_image") ? "ogImage" : detail.field;
                    errorsObj[fieldName] = detail.message;
                });
                setFieldErrors(errorsObj);

                const firstErrorField = errorData.details[0]?.field;
                setTimeout(() => {
                    if (firstErrorField === "name") {
                        nameFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "slug") {
                        slugFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "heading") {
                        headingFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "description") {
                        descriptionFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "displayOrder") {
                        displayOrderFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "categoryImage" || firstErrorField === "image") {
                        imageFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "categoryIcon" || firstErrorField === "icon") {
                        iconFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "ogImage" || firstErrorField === "og_image") {
                        ogImageFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "imageAltText") {
                        imageAltTextFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "canonicalUrl") {
                        canonicalUrlFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "isIndex") {
                        isIndexFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                }, 50);

                showSnackbar(errorData.message || "Validation failed", "error");
            } else {
                const message = err.response?.data?.message || "Failed to save category. Please try again.";
                if (message.toLowerCase().includes("slug")) {
                    setFieldErrors(prev => ({ ...prev, slug: message }));
                    slugFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else if (message.toLowerCase().includes("ogimage") || message.toLowerCase().includes("og image")) {
                    setFieldErrors(prev => ({ ...prev, ogImage: message }));
                    ogImageFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                }
                showSnackbar(message, "error");
            }
        } finally {
            setLoading(false);
        }
    };

    if (pageLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', bgcolor: "#FBFBFB" }}>
                <CircularProgress size={36} sx={{ color: '#6E8964' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#FBFBFB", minHeight: "100vh" }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                    onClick={() => navigate('/category-management')}
                    sx={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '6px',
                        p: 0.75,
                        '&:hover': { backgroundColor: '#F8FAFC' }
                    }}
                >
                    <ArrowLeft size={18} color="#1E293B" />
                </IconButton>
                <Typography sx={{ color: '#1E293B', fontSize: { xs: '1.25rem', md: '1.4rem' }, fontWeight: 500 }}>
                    {categoryId ? "Edit Category" : "Add New Category"}
                </Typography>
            </Box>

            <Paper sx={{ p: { xs: 2.5, md: 4 }, borderRadius: "8px", border: "1px solid #E2E8F0", maxWidth: 740, mx: "auto", bgcolor: '#FFFFFF' }} elevation={0}>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <Box ref={nameFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                Category Name <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter category name"
                                value={name}
                                onChange={handleNameChange}
                                error={Boolean(fieldErrors.name)}
                                helperText={fieldErrors.name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        color: '#1E293B',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                        '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                    },
                                    '& .MuiFormHelperText-root.Mui-error': {
                                        color: '#EF4444',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                        mt: 0.75,
                                        mx: 0,
                                    }
                                }}
                            />
                        </Box>

                        <Box ref={slugFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                Slug <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="e.g. yoga-meditations"
                                value={slug}
                                onChange={handleSlugChange}
                                onBlur={handleSlugBlur}
                                error={Boolean(fieldErrors.slug)}
                                helperText={fieldErrors.slug || "Automatically generated from category name"}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        color: '#1E293B',
                                        fontFamily: 'monospace',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                        '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: fieldErrors.slug ? '#EF4444' : '#64748B',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                        mt: 0.75,
                                        mx: 0,
                                    }
                                }}
                            />
                        </Box>

                        <Box ref={headingFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Heading</Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter heading"
                                value={heading}
                                onChange={(e) => {
                                    setHeading(validateTextField(e.target.value));
                                    if (fieldErrors.heading) {
                                        setFieldErrors(prev => ({ ...prev, heading: null }));
                                    }
                                }}
                                error={Boolean(fieldErrors.heading)}
                                helperText={fieldErrors.heading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        color: '#1E293B',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                        '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                    },
                                    '& .MuiFormHelperText-root.Mui-error': {
                                        color: '#EF4444',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                        mt: 0.75,
                                        mx: 0,
                                    }
                                }}
                            />
                        </Box>

                        <Box ref={descriptionFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Description</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Enter category description (max 100 characters)"
                                value={description}
                                inputProps={{ maxLength: 100 }}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val.length <= 100) {
                                        setDescription(val);
                                        if (fieldErrors.description) {
                                            setFieldErrors(prev => ({ ...prev, description: null }));
                                        }
                                    }
                                }}
                                error={Boolean(fieldErrors.description)}
                                helperText={fieldErrors.description || `${description.length}/100 characters`}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        color: '#1E293B',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                        '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                        mt: 0.5,
                                        mx: 0,
                                        color: fieldErrors.description ? '#EF4444' : '#64748B',
                                        textAlign: 'right',
                                    }
                                }}
                            />
                        </Box>

                        <Box ref={displayOrderFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Display Order</Typography>
                            <TextField
                                fullWidth
                                type="text"
                                inputMode="numeric"
                                placeholder="Enter display order (1 - 999)"
                                value={displayOrder}
                                onKeyDown={(e) => {
                                    if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault();
                                }}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") {
                                        setDisplayOrder("");
                                        setFieldErrors(prev => ({ ...prev, displayOrder: null }));
                                        return;
                                    }
                                    if (/^\d*$/.test(val)) {
                                        setDisplayOrder(val);
                                        if (Number(val) < 1 || Number(val) > 999) {
                                            setFieldErrors(prev => ({ ...prev, displayOrder: "Display order must be between 1 and 999." }));
                                        } else {
                                            setFieldErrors(prev => ({ ...prev, displayOrder: null }));
                                        }
                                    }
                                }}
                                onBlur={() => {
                                    if (displayOrder !== "" && (Number(displayOrder) < 1 || Number(displayOrder) > 999)) {
                                        setFieldErrors(prev => ({ ...prev, displayOrder: "Display order must be between 1 and 999." }));
                                    }
                                }}
                                error={Boolean(fieldErrors.displayOrder)}
                                helperText={fieldErrors.displayOrder}
                                sx={{
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        color: '#1E293B',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                        '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                    },
                                    '& .MuiFormHelperText-root.Mui-error': {
                                        color: '#EF4444',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                        mt: 0.75,
                                        mx: 0,
                                    }
                                }}
                            />
                        </Box>

                        <Box ref={imageFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                Category Image <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            {!imagePreview ? (
                                <Box
                                    component="label"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: fieldErrors.categoryImage ? "1px dashed #EF4444" : "1px dashed #CBD5E1",
                                        borderRadius: "6px",
                                        height: 160,
                                        cursor: "pointer",
                                        bgcolor: "#F8FAFC",
                                        transition: "all 0.2s ease-in-out",
                                        "&:hover": {
                                            borderColor: fieldErrors.categoryImage ? "#EF4444" : "#6E8964",
                                            bgcolor: "#F1F5F9"
                                        }
                                    }}
                                >
                                    <Input
                                        type="file"
                                        inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml, image/*" }}
                                        sx={{ display: "none" }}
                                        onChange={handleImageChange}
                                    />
                                    <Upload size={24} color={fieldErrors.categoryImage ? "#EF4444" : "#64748B"} style={{ marginBottom: 6 }} />
                                    <Typography sx={{ color: fieldErrors.categoryImage ? "#EF4444" : "#475569", fontSize: '0.8rem', fontWeight: 500 }}>
                                        Click to upload image
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: fieldErrors.categoryImage ? "#EF4444" : "#94A3B8", fontSize: '0.7rem' }}>
                                        PNG, JPG or WebP (Max 2MB)
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ position: "relative", width: "100%", height: 160, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                    <Box
                                        component="img"
                                        src={imagePreview}
                                        alt="Preview"
                                        sx={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                        }}
                                    />
                                    <IconButton
                                        onClick={handleRemoveImage}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E2E8F0",
                                            color: "#EF4444",
                                            p: 0.5,
                                            '&:hover': { bgcolor: '#FEE2E2' }
                                        }}
                                        size="small"
                                    >
                                        <X size={15} />
                                    </IconButton>
                                </Box>
                            )}
                            <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                * At least one dimension of the image must be ≥ 400px.
                            </Typography>
                            {fieldErrors.categoryImage && (
                                <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                    {fieldErrors.categoryImage}
                                </Typography>
                            )}
                        </Box>

                        <Box ref={iconFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                Category Icon <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            {!iconPreview ? (
                                <Box
                                    component="label"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: fieldErrors.categoryIcon ? "1px dashed #EF4444" : "1px dashed #CBD5E1",
                                        borderRadius: "6px",
                                        height: 160,
                                        cursor: "pointer",
                                        bgcolor: "#F8FAFC",
                                        transition: "all 0.2s ease-in-out",
                                        "&:hover": {
                                            borderColor: fieldErrors.categoryIcon ? "#EF4444" : "#6E8964",
                                            bgcolor: "#F1F5F9"
                                        }
                                    }}
                                >
                                    <Input
                                        type="file"
                                        inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml, image/*" }}
                                        sx={{ display: "none" }}
                                        onChange={handleIconChange}
                                    />
                                    <Upload size={24} color={fieldErrors.categoryIcon ? "#EF4444" : "#64748B"} style={{ marginBottom: 6 }} />
                                    <Typography sx={{ color: fieldErrors.categoryIcon ? "#EF4444" : "#475569", fontSize: '0.8rem', fontWeight: 500 }}>
                                        Click to upload icon
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: fieldErrors.categoryIcon ? "#EF4444" : "#94A3B8", fontSize: '0.7rem' }}>
                                        PNG, JPG or WebP (Max 2MB)
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ position: "relative", width: "100%", height: 160, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                    <Box
                                        component="img"
                                        src={iconPreview}
                                        alt="Icon Preview"
                                        sx={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                        }}
                                    />
                                    <IconButton
                                        onClick={handleRemoveIcon}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E2E8F0",
                                            color: "#EF4444",
                                            p: 0.5,
                                            '&:hover': { bgcolor: '#FEE2E2' }
                                        }}
                                        size="small"
                                    >
                                        <X size={15} />
                                    </IconButton>
                                </Box>
                            )}
                            <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                * At least one dimension of the icon must be ≥ 400px.
                            </Typography>
                            {fieldErrors.categoryIcon && (
                                <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                    {fieldErrors.categoryIcon}
                                </Typography>
                            )}
                        </Box>

                        {/* Suitability Items Section */}
                        <Box sx={{ pt: 2, borderTop: '1px solid #F1F5F9' }}>
                            <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircle2 size={16} color="#6E8964" /> Suitability Items
                            </Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 2 }}>
                                Add suitability guidelines or bullet points for this category.
                            </Typography>

                            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                                <TextField
                                    placeholder="Add suitability item (e.g. Suitable for beginners)..."
                                    value={newSuitabilityInput}
                                    onChange={(e) => setNewSuitabilityInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddSuitabilityItem();
                                        }
                                    }}
                                    fullWidth
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.8rem',
                                            bgcolor: '#FFFFFF',
                                            '& fieldset': { borderColor: '#E2E8F0' },
                                            '&:hover fieldset': { borderColor: '#CBD5E1' },
                                            '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<Plus size={16} />}
                                    onClick={handleAddSuitabilityItem}
                                    sx={{
                                        backgroundColor: '#1E293B',
                                        color: '#FFFFFF',
                                        textTransform: 'none',
                                        boxShadow: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        px: 2.5,
                                        '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
                                    }}
                                >
                                    Add
                                </Button>
                            </Stack>

                            <Stack spacing={1}>
                                {suitabilityItems.map((item, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            p: 1.25,
                                            backgroundColor: '#F8FAFC',
                                            border: '1px solid #E2E8F0',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        <Typography sx={{ color: '#334155', fontSize: '0.8rem', fontWeight: 500 }}>
                                            • {typeof item === 'object' && item !== null ? item.name || item.title || item.label || '' : item}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveSuitabilityItem(idx)}
                                            sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                        >
                                            <Trash2 size={15} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>

                        {/* FAQs Section */}
                        <Box sx={{ pt: 2, borderTop: '1px solid #F1F5F9' }}>
                            <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HelpCircle size={16} color="#6E8964" /> Frequently Asked Questions (FAQs)
                            </Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 2 }}>
                                Add FAQs relevant to this category.
                            </Typography>

                            <Card variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', boxShadow: 'none' }}>
                                <Stack spacing={1.5}>
                                    <TextField
                                        label="Question"
                                        placeholder="e.g. What should I prepare for this category?"
                                        value={newFaqQuestion}
                                        onChange={(e) => setNewFaqQuestion(e.target.value)}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            bgcolor: '#FFFFFF',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.8rem',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.8rem', color: '#64748B' },
                                            '& .MuiInputLabel-root.Mui-focused': { color: '#6E8964' }
                                        }}
                                    />
                                    <TextField
                                        label="Answer"
                                        placeholder="e.g. Bring comfortable clothing and open mind."
                                        value={newFaqAnswer}
                                        onChange={(e) => setNewFaqAnswer(e.target.value)}
                                        multiline
                                        rows={2}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            bgcolor: '#FFFFFF',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.8rem',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.8rem', color: '#64748B' },
                                            '& .MuiInputLabel-root.Mui-focused': { color: '#6E8964' }
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<Plus size={15} />}
                                            onClick={handleAddFaq}
                                            sx={{
                                                backgroundColor: '#6E8964',
                                                color: '#FFFFFF',
                                                textTransform: 'none',
                                                boxShadow: 'none',
                                                borderRadius: '6px',
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                                px: 2,
                                                '&:hover': { backgroundColor: '#5A7352', boxShadow: 'none' }
                                            }}
                                        >
                                            Add FAQ
                                        </Button>
                                    </Box>
                                </Stack>
                            </Card>

                            <Stack spacing={1.5}>
                                {faqs.map((faq, idx) => (
                                    <Paper
                                        key={idx}
                                        variant="outlined"
                                        sx={{ p: 1.75, borderRadius: '6px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none' }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box sx={{ pr: 2 }}>
                                                <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.8rem' }}>
                                                    Q: {typeof faq?.question === 'object' && faq.question !== null ? faq.question.name || '' : (faq?.question || '')}
                                                </Typography>
                                                <Typography sx={{ color: '#64748B', mt: 0.5, fontSize: '0.775rem' }}>
                                                    A: {typeof faq?.answer === 'object' && faq.answer !== null ? faq.answer.name || '' : (faq?.answer || '')}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveFaq(idx)}
                                                sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                            >
                                                <Trash2 size={15} />
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>

                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={status}
                                        onChange={(e) => setStatus(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#6E8964',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#6E8964',
                                            },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontWeight: 500, fontSize: '0.825rem', color: "#334155" }}>Active Status</Typography>}
                            />
                        </Box>

                        {/* SEO Section */}
                        <Box sx={{ pt: 2, borderTop: '1px solid #F1F5F9' }}>
                            <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem', mb: 0.5 }}>
                                SEO
                            </Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 2 }}>
                                Configure SEO settings for this category.
                            </Typography>

                            <Stack spacing={2.5}>
                                <Box ref={metaTitleFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Meta Title</Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter meta title"
                                        value={metaTitle}
                                        onChange={(e) => {
                                            setMetaTitle(validateTextField(e.target.value));
                                            if (fieldErrors.metaTitle) {
                                                setFieldErrors(prev => ({ ...prev, metaTitle: null }));
                                            }
                                        }}
                                        error={Boolean(fieldErrors.metaTitle)}
                                        helperText={fieldErrors.metaTitle}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                color: '#1E293B',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                                '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                            },
                                            '& .MuiFormHelperText-root.Mui-error': {
                                                color: '#EF4444',
                                                fontSize: '0.75rem',
                                                fontWeight: 400,
                                                mt: 0.75,
                                                mx: 0,
                                            }
                                        }}
                                    />
                                </Box>

                                <Box ref={metaDescriptionFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Meta Description</Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder="Enter meta description"
                                        value={metaDescription}
                                        onChange={(e) => {
                                            setMetaDescription(e.target.value);
                                            if (fieldErrors.metaDescription) {
                                                setFieldErrors(prev => ({ ...prev, metaDescription: null }));
                                            }
                                        }}
                                        error={Boolean(fieldErrors.metaDescription)}
                                        helperText={fieldErrors.metaDescription}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                color: '#1E293B',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                                '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                            },
                                            '& .MuiFormHelperText-root.Mui-error': {
                                                color: '#EF4444',
                                                fontSize: '0.75rem',
                                                fontWeight: 400,
                                                mt: 0.75,
                                                mx: 0,
                                            }
                                        }}
                                    />
                                </Box>

                                <Box ref={metaOutletFieldRef}>
                                    <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle2 size={16} color="#6E8964" /> Meta Outlet (H2  Outlet)
                                    </Typography>
                                    <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 2 }}>
                                        Add meta outlet bullet points for this category.
                                    </Typography>

                                    <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                                        <TextField
                                            placeholder="Add meta outlet item..."
                                            value={newMetaOutletInput}
                                            onChange={(e) => setNewMetaOutletInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddMetaOutletItem();
                                                }
                                            }}
                                            fullWidth
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.8rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E2E8F0' },
                                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                    '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<Plus size={16} />}
                                            onClick={handleAddMetaOutletItem}
                                            sx={{
                                                backgroundColor: '#1E293B',
                                                color: '#FFFFFF',
                                                textTransform: 'none',
                                                boxShadow: 'none',
                                                borderRadius: '6px',
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                                px: 2.5,
                                                '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
                                            }}
                                        >
                                            Add
                                        </Button>
                                    </Stack>

                                    <Stack spacing={1}>
                                        {metaOutlet.map((item, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    p: 1.25,
                                                    backgroundColor: '#F8FAFC',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px'
                                                }}
                                            >
                                                <Typography sx={{ color: '#334155', fontSize: '0.8rem', fontWeight: 500 }}>
                                                    • {item}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveMetaOutletItem(idx)}
                                                    sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                                >
                                                    <Trash2 size={15} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>

                                <Box ref={ogImageFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                        OG Image
                                    </Typography>
                                    {!ogImagePreview ? (
                                        <Box
                                            component="label"
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                border: fieldErrors.ogImage ? "1px dashed #EF4444" : "1px dashed #CBD5E1",
                                                borderRadius: "6px",
                                                height: 160,
                                                cursor: "pointer",
                                                bgcolor: "#F8FAFC",
                                                transition: "all 0.2s ease-in-out",
                                                "&:hover": {
                                                    borderColor: fieldErrors.ogImage ? "#EF4444" : "#6E8964",
                                                    bgcolor: "#F1F5F9"
                                                }
                                            }}
                                        >
                                            <Input
                                                type="file"
                                                inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml, image/*" }}
                                                sx={{ display: "none" }}
                                                onChange={handleOgImageChange}
                                            />
                                            <Upload size={24} color={fieldErrors.ogImage ? "#EF4444" : "#64748B"} style={{ marginBottom: 6 }} />
                                            <Typography sx={{ color: fieldErrors.ogImage ? "#EF4444" : "#475569", fontSize: '0.8rem', fontWeight: 500 }}>
                                                Click to upload OG image
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: fieldErrors.ogImage ? "#EF4444" : "#94A3B8", fontSize: '0.7rem' }}>
                                                PNG, JPG or WebP (Max 2MB)
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ position: "relative", width: "100%", height: 160, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                            <Box
                                                component="img"
                                                src={ogImagePreview}
                                                alt="OG Image Preview"
                                                sx={{
                                                    maxWidth: "100%",
                                                    maxHeight: "100%",
                                                    objectFit: "contain",
                                                }}
                                            />
                                            <IconButton
                                                onClick={handleRemoveOgImage}
                                                sx={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                    backgroundColor: "#FFFFFF",
                                                    border: "1px solid #E2E8F0",
                                                    color: "#EF4444",
                                                    p: 0.5,
                                                    '&:hover': { bgcolor: '#FEE2E2' }
                                                }}
                                                size="small"
                                            >
                                                <X size={15} />
                                            </IconButton>
                                        </Box>
                                    )}
                                    {fieldErrors.ogImage && (
                                        <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                            {fieldErrors.ogImage}
                                        </Typography>
                                    )}
                                </Box>

                                <Box ref={imageAltTextFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Image Alt Text</Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter image alt text"
                                        value={imageAltText}
                                        onChange={(e) => {
                                            setImageAltText(validateTextField(e.target.value));
                                            if (fieldErrors.imageAltText) {
                                                setFieldErrors(prev => ({ ...prev, imageAltText: null }));
                                            }
                                        }}
                                        error={Boolean(fieldErrors.imageAltText)}
                                        helperText={fieldErrors.imageAltText}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                color: '#1E293B',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                                '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                            },
                                            '& .MuiFormHelperText-root.Mui-error': {
                                                color: '#EF4444',
                                                fontSize: '0.75rem',
                                                fontWeight: 400,
                                                mt: 0.75,
                                                mx: 0,
                                            }
                                        }}
                                    />
                                </Box>

                                <Box ref={canonicalUrlFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Canonical URL</Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter canonical URL"
                                        value={canonicalUrl}
                                        onChange={(e) => {
                                            setCanonicalUrl(validateTextField(e.target.value));
                                            if (fieldErrors.canonicalUrl) {
                                                setFieldErrors(prev => ({ ...prev, canonicalUrl: null }));
                                            }
                                        }}
                                        error={Boolean(fieldErrors.canonicalUrl)}
                                        helperText={fieldErrors.canonicalUrl}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                color: '#1E293B',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                                '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                            },
                                            '& .MuiFormHelperText-root.Mui-error': {
                                                color: '#EF4444',
                                                fontSize: '0.75rem',
                                                fontWeight: 400,
                                                mt: 0.75,
                                                mx: 0,
                                            }
                                        }}
                                    />
                                </Box>

                                <Box ref={isIndexFieldRef}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(isIndex)}
                                                onChange={(e) => {
                                                    setIsIndex(e.target.checked);
                                                    if (fieldErrors.isIndex) {
                                                        setFieldErrors(prev => ({ ...prev, isIndex: null }));
                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: '#6E8964',
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: '#6E8964',
                                                    },
                                                }}
                                            />
                                        }
                                        label={<Typography sx={{ fontWeight: 500, fontSize: '0.825rem', color: "#334155" }}>Is Index</Typography>}
                                    />
                                    {fieldErrors.isIndex && (
                                        <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                            {fieldErrors.isIndex}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #F1F5F9' }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/category-management')}
                                disabled={loading}
                                sx={{
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    borderColor: '#E2E8F0',
                                    color: '#475569',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    px: 3,
                                    py: 0.85,
                                    '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading || success}
                                sx={{
                                    bgcolor: '#6E8964',
                                    color: '#FFFFFF',
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    px: 3.5,
                                    py: 0.85,
                                    '&:hover': { bgcolor: '#5A7352', boxShadow: 'none' },
                                    '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' }
                                }}
                            >
                                {loading ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : (categoryId ? "Update Category" : "Create Category")}
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Paper>
        </Box>
    );
};

export default AddEditCategories;
